import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type AnimatedComponent, animated, useSpring } from '@react-spring/web';
import { AddToFavoriesAriaText, RemoveFromFavoriesAriaText } from 'autogen/translation-keys/trans-website-product';
import { ButtonWithIcon } from 'components/Button';
import type IButton from 'components/Button/shared/types/button';
import Icon from 'components/Icon';
import type { TrackingCategory } from 'modules/tracking/UAEventTracking';
import { customVariantsStyling, filledHeart } from './shared/styles';
import useFavoriteItem from './useFavoriteItem';

const customVariants = ['favoriteGhost', 'favoriteGhostHorizontal', 'chanel'] as const;
type CustomVariantsTypes = (typeof customVariants)[number];

type ModifiedIconSize = 'md' | 'lg';
type ExpandedButtonVariantKeys = IButton['variant'] | CustomVariantsTypes;

// #region Styled

const StyledButtonWithIcon = styled<any>(ButtonWithIcon)<{
    $isFavorite: boolean;
    $variant: IFavoriteButton['variant'];
}>`
    ${({ $variant, $isFavorite }) =>
        $variant && ($isFavorite ? customVariantsStyling[$variant].favorite : customVariantsStyling[$variant].default)};
`;

const AnimatedContainer: AnimatedComponent<any> = animated.div;

// #endregion Styled

export interface IProductFavoriteButton extends Omit<IButton, 'variant'> {
    variant?: ExpandedButtonVariantKeys;
    initialFavoriteState: boolean;
    productId: number;
    uaTrackingCategory?: TrackingCategory;
    iconSize?: ModifiedIconSize;
}

export interface IFavoriteButton extends Omit<IButton, 'variant'> {
    variant?: ExpandedButtonVariantKeys;
    isFavorite?: boolean;
    iconSize?: ModifiedIconSize;
}

export function FavoriteButton({ isFavorite, variant = 'ghostOnLight', ...restProps }: IFavoriteButton) {
    const { t } = useTranslation();
    const { iconSize } = restProps;
    const { x } = useSpring({
        x: isFavorite ? 1 : 0,
        config: { duration: 1000 },
        reset: true,
        immediate: !isFavorite
    });

    const isCustomVariant = customVariants.includes(variant as CustomVariantsTypes); // TS hack

    return (
        <StyledButtonWithIcon
            {...restProps}
            $variant={isCustomVariant ? variant : undefined}
            $isFavorite={isFavorite}
            variant={isCustomVariant ? 'ghostOnLight' : variant}
            aria-label={isFavorite ? t(RemoveFromFavoriesAriaText) : t(AddToFavoriesAriaText)}
            icon={
                <AnimatedContainer
                    style={{
                        display: 'flex',
                        scale: x.to({
                            range: [0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 1],
                            output: [1, 1.6, 0.6, 1.1, 0.9, 1.1, 1.03, 1]
                        })
                    }}
                >
                    {isFavorite ? (
                        <Icon type="HeartFilled" size={iconSize === 'lg' ? 24 : 16} />
                    ) : (
                        <Icon type="Heart" size={iconSize === 'lg' ? 24 : 16} />
                    )}
                </AnimatedContainer>
            }
        />
    );
}

const ProductFavoriteButton = ({
    initialFavoriteState,
    productId,
    onClick,
    variant = 'ghostOnLight',
    uaTrackingCategory,
    ...restProps
}: IProductFavoriteButton) => {
    const { isItemFavorite, handleFavoriteItem } = useFavoriteItem(initialFavoriteState, productId);

    function handleClick(e) {
        handleFavoriteItem(uaTrackingCategory);
        if (typeof onClick === 'function') {
            onClick(e);
        }
    }

    return (
        <FavoriteButton {...restProps} variant={variant} isFavorite={isItemFavorite} onClick={(e) => handleClick(e)} />
    );
};

export default memo(ProductFavoriteButton);
